import React, { useEffect, useState } from 'react';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import Config from '@go-client/lib/config';
import { BubbleButton, BubbleTable } from '@go-client/components/BubbleUI';
import { exportBrandSettings } from '@go-client/lib/api/brand';
import { usePublicServerInfo } from '@go-client/context/PublicServerInfoContext';
import {
  DateGroup,
  dateToTimeLocal,
  DEFAULT_HOUR_FORMAT,
  DEFAULT_TIMESTR_FORMAT,
  getGODateLocal,
  dateToIsoUTC,
  minutesToTime,
  timeToMinutes,
  timeToString,
} from '@go-shared/lib/time';
import { usePropertyInfo } from '@go-client/context/PropertyInfoContext';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import { DEFAULT_DAY_START } from '@go-shared/lib/property';
import { notUndefined } from '@go-shared/lib/functional';
import { resolveProperty } from './MenuBar';
import { BubbleTableFieldBehavior } from './BubbleUI/BubbleTable';

async function downloadBrandSettings(id: string) {
  const result = await exportBrandSettings(id);

  if (result.location) window.open(result.location, '_blank');
}

export default function ServerAtAGlance() {
  const { serverDate, fetchedAt } = usePublicServerInfo();
  const serverInfo = useServerInfo();
  const { myProperties } = usePropertyInfo();
  const { user } = useUserInfo();

  const { brand, plugins } = serverInfo;

  const property = resolveProperty(user, serverInfo, myProperties);
  let goDate: DateGroup | undefined;

  if (property) {
    const dayStart = property.dayStart || DEFAULT_DAY_START;
    const dayStartMinutes = timeToMinutes(dayStart);

    goDate = getGODateLocal(dayStartMinutes, serverDate);
  }

  const [updatedMinutesAgo, setUpdatedMinutesAgo] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setUpdatedMinutesAgo(Math.floor((Date.now() - fetchedAt) / 1000 / 60));
    }, 60000);

    return () => clearInterval(interval);
  }, [fetchedAt]);

  let infoToShow = [
    {
      key: 'go-version',
      label: 'GO Version',
      value: <span>{Config.VERSION}</span>,
    },
    {
      key: 'local-date',
      label: 'Device Time',
      value: (
        <div>
          <div>
            <span>{dateToIsoUTC()}&nbsp;</span>
            <span>
              {timeToString(dateToTimeLocal(), DEFAULT_HOUR_FORMAT, DEFAULT_TIMESTR_FORMAT)}
            </span>
          </div>
        </div>
      ),
    },
    {
      key: 'server-date',
      label: 'Server Time',
      value: (
        <div>
          <div>
            <span>{serverDate.iso}&nbsp;</span>
            <span>
              {timeToString(
                minutesToTime(serverDate.minutes),
                DEFAULT_HOUR_FORMAT,
                DEFAULT_TIMESTR_FORMAT
              )}
            </span>
          </div>
          <div className="text-smoke text-xs italic">Updated ~{updatedMinutesAgo} minutes ago</div>
        </div>
      ),
    },
    goDate
      ? {
          key: 'go-date',
          label: 'GO Time',
          value: (
            <div>
              <div>
                <span>{goDate.iso}&nbsp;</span>
                <span>
                  {timeToString(
                    minutesToTime(goDate.minutes),
                    DEFAULT_HOUR_FORMAT,
                    DEFAULT_TIMESTR_FORMAT
                  )}
                </span>
              </div>
              <div className="text-smoke text-xs italic">
                Updated ~{updatedMinutesAgo} minutes ago
              </div>
            </div>
          ),
        }
      : undefined,
    {
      key: 'enabled-plugins',
      label: 'Plugins',
      value: <span>{plugins.map(p => p.name).join(', ')}</span>,
    },
  ].filter(notUndefined);

  infoToShow = infoToShow.concat(
    (brand.settings?.notes ?? []).map(bb => {
      return {
        key: bb._id,
        label: bb.label,
        value: bb.value.match(/^https?:\/\/.*$/) ? (
          <a className="underline text-navy" href={`${bb.value}`} target="_blank">
            {bb.value}
          </a>
        ) : (
          <span>{bb.value}</span>
        ),
      };
    })
  );

  return (
    <>
      <BubbleTable
        responsive
        striped
        hideHeader
        slim
        bordered
        items={infoToShow}
        fields={[
          {
            key: 'label',
            behavior: BubbleTableFieldBehavior.Condense,
            tdProps: { className: 'font-semibold' },
          },
          { key: 'value' },
        ]}
        className="mb-4"
      />
      <BubbleButton
        className="text-left"
        icon="file-export"
        variant="navy"
        onClick={() => downloadBrandSettings(brand._id)}
      >
        Download Brand Settings
      </BubbleButton>
    </>
  );
}
